// Noble gas (shorthand) electron configurations
export const nobleGasConfig: Record<number, string> = {
  1: "1s¹",
  2: "1s²",
  3: "[He] 2s¹",
  4: "[He] 2s²",
  5: "[He] 2s² 2p¹",
  6: "[He] 2s² 2p²",
  7: "[He] 2s² 2p³",
  8: "[He] 2s² 2p⁴",
  9: "[He] 2s² 2p⁵",
  10: "[He] 2s² 2p⁶",
  11: "[Ne] 3s¹",
  12: "[Ne] 3s²",
  13: "[Ne] 3s² 3p¹",
  14: "[Ne] 3s² 3p²",
  15: "[Ne] 3s² 3p³",
  16: "[Ne] 3s² 3p⁴",
  17: "[Ne] 3s² 3p⁵",
  18: "[Ne] 3s² 3p⁶",
  19: "[Ar] 4s¹",
  20: "[Ar] 4s²",
  21: "[Ar] 3d¹ 4s²",
  22: "[Ar] 3d² 4s²",
  23: "[Ar] 3d³ 4s²",
  24: "[Ar] 3d⁵ 4s¹",
  25: "[Ar] 3d⁵ 4s²",
  26: "[Ar] 3d⁶ 4s²",
  27: "[Ar] 3d⁷ 4s²",
  28: "[Ar] 3d⁸ 4s²",
  29: "[Ar] 3d¹⁰ 4s¹",
  30: "[Ar] 3d¹⁰ 4s²",
  31: "[Ar] 3d¹⁰ 4s² 4p¹",
  32: "[Ar] 3d¹⁰ 4s² 4p²",
  33: "[Ar] 3d¹⁰ 4s² 4p³",
  34: "[Ar] 3d¹⁰ 4s² 4p⁴",
  35: "[Ar] 3d¹⁰ 4s² 4p⁵",
  36: "[Kr]",
  37: "[Kr] 5s¹",
  38: "[Kr] 5s²",
  39: "[Kr] 4d¹ 5s²",
  40: "[Kr] 4d² 5s²",
  41: "[Kr] 4d⁴ 5s¹",
  42: "[Kr] 4d⁵ 5s¹",
  43: "[Kr] 4d⁵ 5s²",
  44: "[Kr] 4d⁷ 5s¹",
  45: "[Kr] 4d⁸ 5s¹",
  46: "[Kr] 4d¹⁰",
  47: "[Kr] 4d¹⁰ 5s¹",
  48: "[Kr] 4d¹⁰ 5s²",
  49: "[Kr] 4d¹⁰ 5s² 5p¹",
  50: "[Kr] 4d¹⁰ 5s² 5p²",
  51: "[Kr] 4d¹⁰ 5s² 5p³",
  52: "[Kr] 4d¹⁰ 5s² 5p⁴",
  53: "[Kr] 4d¹⁰ 5s² 5p⁵",
  54: "[Xe]",
  55: "[Xe] 6s¹",
  56: "[Xe] 6s²",
  79: "[Xe] 4f¹⁴ 5d¹⁰ 6s¹",
  80: "[Xe] 4f¹⁴ 5d¹⁰ 6s²",
  82: "[Xe] 4f¹⁴ 5d¹⁰ 6s² 6p²",
  86: "[Rn]",
  92: "[Rn] 5f³ 6d¹ 7s²",
};

// Principal quantum numbers for last electron
export interface QuantumNumbers {
  n: number;
  l: number;
  ml: number;
  ms: string;
  sublabel: string; // e.g. "3d"
}

export const quantumNumbers: Record<number, QuantumNumbers> = {
  1: { n: 1, l: 0, ml: 0, ms: "+½", sublabel: "1s" },
  2: { n: 1, l: 0, ml: 0, ms: "−½", sublabel: "1s" },
  3: { n: 2, l: 0, ml: 0, ms: "+½", sublabel: "2s" },
  4: { n: 2, l: 0, ml: 0, ms: "−½", sublabel: "2s" },
  5: { n: 2, l: 1, ml: -1, ms: "+½", sublabel: "2p" },
  6: { n: 2, l: 1, ml: 0, ms: "+½", sublabel: "2p" },
  7: { n: 2, l: 1, ml: 1, ms: "+½", sublabel: "2p" },
  8: { n: 2, l: 1, ml: -1, ms: "−½", sublabel: "2p" },
  9: { n: 2, l: 1, ml: 0, ms: "−½", sublabel: "2p" },
  10: { n: 2, l: 1, ml: 1, ms: "−½", sublabel: "2p" },
  11: { n: 3, l: 0, ml: 0, ms: "+½", sublabel: "3s" },
  12: { n: 3, l: 0, ml: 0, ms: "−½", sublabel: "3s" },
  13: { n: 3, l: 1, ml: -1, ms: "+½", sublabel: "3p" },
  14: { n: 3, l: 1, ml: 0, ms: "+½", sublabel: "3p" },
  15: { n: 3, l: 1, ml: 1, ms: "+½", sublabel: "3p" },
  16: { n: 3, l: 1, ml: -1, ms: "−½", sublabel: "3p" },
  17: { n: 3, l: 1, ml: 0, ms: "−½", sublabel: "3p" },
  18: { n: 3, l: 1, ml: 1, ms: "−½", sublabel: "3p" },
  19: { n: 4, l: 0, ml: 0, ms: "+½", sublabel: "4s" },
  20: { n: 4, l: 0, ml: 0, ms: "−½", sublabel: "4s" },
  21: { n: 3, l: 2, ml: -2, ms: "+½", sublabel: "3d" },
  22: { n: 3, l: 2, ml: -1, ms: "+½", sublabel: "3d" },
  23: { n: 3, l: 2, ml: 0, ms: "+½", sublabel: "3d" },
  24: { n: 3, l: 2, ml: 2, ms: "+½", sublabel: "3d" },
  25: { n: 3, l: 2, ml: 1, ms: "+½", sublabel: "3d" },
  26: { n: 3, l: 2, ml: -2, ms: "−½", sublabel: "3d" },
  27: { n: 3, l: 2, ml: -1, ms: "−½", sublabel: "3d" },
  28: { n: 3, l: 2, ml: 0, ms: "−½", sublabel: "3d" },
  29: { n: 3, l: 2, ml: 2, ms: "−½", sublabel: "3d" },
  30: { n: 3, l: 2, ml: 1, ms: "−½", sublabel: "3d" },
  35: { n: 4, l: 1, ml: 0, ms: "−½", sublabel: "4p" },
  36: { n: 4, l: 1, ml: 1, ms: "−½", sublabel: "4p" },
  53: { n: 5, l: 1, ml: 0, ms: "−½", sublabel: "5p" },
};

// Reactivity info
export interface ReactivityInfo {
  level: "Very High" | "High" | "Moderate" | "Low" | "Very Low" | "Inert";
  description: string;
}

export const reactivityData: Record<number, ReactivityInfo> = {
  1: { level: "High", description: "Highly flammable; forms explosive mixtures with air. Reacts with most nonmetals." },
  2: { level: "Inert", description: "Noble gas - extremely stable, does not form compounds under normal conditions." },
  3: { level: "Very High", description: "Reacts vigorously with water and air. Must be stored in mineral oil." },
  4: { level: "High", description: "Reacts with water (slowly) and acids. Burns in air to form BeO." },
  5: { level: "Low", description: "Relatively inert at room temperature. Reacts at high temperatures with metals and nonmetals." },
  6: { level: "Moderate", description: "Forms compounds with nearly all elements. Burns in oxygen; basis of organic chemistry." },
  7: { level: "Low", description: "Triple bond makes N₂ very stable. Requires high energy to react (lightning, Haber process)." },
  8: { level: "High", description: "Highly reactive oxidizer. Reacts with most elements, supporting combustion." },
  9: { level: "Very High", description: "Most reactive element - reacts with almost every substance, including noble gases." },
  10: { level: "Inert", description: "Noble gas - does not react under normal conditions." },
  11: { level: "Very High", description: "Reacts explosively with water. Tarnishes rapidly in air. Stored under oil." },
  12: { level: "High", description: "Burns with a brilliant white flame. Reacts with acids and slowly with water." },
  13: { level: "Moderate", description: "Protective oxide layer prevents further reaction. Dissolves in strong acids/bases." },
  14: { level: "Low", description: "Relatively inert at room temperature. Reacts with halogens and strong bases when heated." },
  15: { level: "High", description: "White phosphorus is extremely reactive and pyrophoric. Red phosphorus is more stable." },
  16: { level: "Moderate", description: "Combines with most elements. Burns in air to form SO₂." },
  17: { level: "Very High", description: "Powerful oxidizer. Reacts with most elements and many compounds." },
  18: { level: "Inert", description: "Noble gas - chemically inert under standard conditions." },
  19: { level: "Very High", description: "More reactive than sodium - explodes on contact with water. Stored under oil." },
  20: { level: "High", description: "Reacts with water and acids. Burns in air. Less reactive than alkali metals." },
  24: { level: "Moderate", description: "Resistant to corrosion due to oxide layer. Reacts with dilute acids." },
  25: { level: "Moderate", description: "Reacts with dilute acids. Slowly tarnishes in moist air." },
  26: { level: "Moderate", description: "Rusts in moist air. Reacts with dilute acids to release hydrogen." },
  27: { level: "Low", description: "Slowly oxidized in air. Dissolves in dilute acids." },
  28: { level: "Low", description: "Resistant to corrosion. Reacts slowly with acids." },
  29: { level: "Low", description: "Does not react with water. Slowly reacts with atmospheric oxygen (patina)." },
  30: { level: "Moderate", description: "Reacts with acids and strong bases (amphoteric). Tarnishes in moist air." },
  35: { level: "Very High", description: "Highly reactive halogen. Corrosive liquid; vapors are toxic." },
  36: { level: "Inert", description: "Noble gas - generally inert; a few compounds known under extreme conditions." },
  47: { level: "Low", description: "Does not react with water or most acids. Tarnishes with sulfur compounds." },
  53: { level: "High", description: "Reactive halogen, though less so than Cl₂ or Br₂. Sublimes to purple vapor." },
  79: { level: "Very Low", description: "Most noble of metals - resists nearly all acids except aqua regia." },
  82: { level: "Low", description: "Relatively unreactive. Slowly corrodes in moist air. Dissolves in HNO₃." },
  92: { level: "High", description: "Radioactive. Reacts with water, acids, and oxygen. Pyrophoric as fine powder." },
};

// Comprehensive isotope data
export interface IsotopeInfo {
  symbol: string;
  massNumber: number;
  neutrons: number;
  abundance?: string;
  halfLife?: string;
  decayMode?: string;
  stable: boolean;
}

export const isotopeData: Record<number, IsotopeInfo[]> = {
  1: [
    { symbol: "¹H (Protium)", massNumber: 1, neutrons: 0, abundance: "99.9855%", stable: true },
    { symbol: "²H (Deuterium)", massNumber: 2, neutrons: 1, abundance: "0.0145%", stable: true },
    { symbol: "³H (Tritium)", massNumber: 3, neutrons: 2, halfLife: "12.32 years", decayMode: "β⁻", stable: false },
  ],
  2: [
    { symbol: "³He", massNumber: 3, neutrons: 1, abundance: "0.0002%", stable: true },
    { symbol: "⁴He", massNumber: 4, neutrons: 2, abundance: "99.9998%", stable: true },
  ],
  3: [
    { symbol: "⁶Li", massNumber: 6, neutrons: 3, abundance: "7.59%", stable: true },
    { symbol: "⁷Li", massNumber: 7, neutrons: 4, abundance: "92.41%", stable: true },
  ],
  4: [
    { symbol: "⁹Be", massNumber: 9, neutrons: 5, abundance: "100%", stable: true },
    { symbol: "⁷Be", massNumber: 7, neutrons: 3, halfLife: "53.22 days", decayMode: "EC", stable: false },
    { symbol: "¹⁰Be", massNumber: 10, neutrons: 6, halfLife: "1.39×10⁶ y", decayMode: "β⁻", stable: false },
  ],
  5: [
    { symbol: "¹⁰B", massNumber: 10, neutrons: 5, abundance: "19.9%", stable: true },
    { symbol: "¹¹B", massNumber: 11, neutrons: 6, abundance: "80.1%", stable: true },
  ],
  6: [
    { symbol: "¹²C", massNumber: 12, neutrons: 6, abundance: "98.93%", stable: true },
    { symbol: "¹³C", massNumber: 13, neutrons: 7, abundance: "1.07%", stable: true },
    { symbol: "¹⁴C", massNumber: 14, neutrons: 8, halfLife: "5,730 years", decayMode: "β⁻", stable: false },
  ],
  7: [
    { symbol: "¹⁴N", massNumber: 14, neutrons: 7, abundance: "99.636%", stable: true },
    { symbol: "¹⁵N", massNumber: 15, neutrons: 8, abundance: "0.364%", stable: true },
  ],
  8: [
    { symbol: "¹⁶O", massNumber: 16, neutrons: 8, abundance: "99.757%", stable: true },
    { symbol: "¹⁷O", massNumber: 17, neutrons: 9, abundance: "0.038%", stable: true },
    { symbol: "¹⁸O", massNumber: 18, neutrons: 10, abundance: "0.205%", stable: true },
  ],
  9: [
    { symbol: "¹⁹F", massNumber: 19, neutrons: 10, abundance: "100%", stable: true },
    { symbol: "¹⁸F", massNumber: 18, neutrons: 9, halfLife: "109.77 min", decayMode: "β⁺", stable: false },
  ],
  10: [
    { symbol: "²⁰Ne", massNumber: 20, neutrons: 10, abundance: "90.48%", stable: true },
    { symbol: "²¹Ne", massNumber: 21, neutrons: 11, abundance: "0.27%", stable: true },
    { symbol: "²²Ne", massNumber: 22, neutrons: 12, abundance: "9.25%", stable: true },
  ],
  11: [
    { symbol: "²³Na", massNumber: 23, neutrons: 12, abundance: "100%", stable: true },
    { symbol: "²²Na", massNumber: 22, neutrons: 11, halfLife: "2.602 years", decayMode: "β⁺", stable: false },
    { symbol: "²⁴Na", massNumber: 24, neutrons: 13, halfLife: "14.96 hours", decayMode: "β⁻", stable: false },
  ],
  12: [
    { symbol: "²⁴Mg", massNumber: 24, neutrons: 12, abundance: "78.99%", stable: true },
    { symbol: "²⁵Mg", massNumber: 25, neutrons: 13, abundance: "10.00%", stable: true },
    { symbol: "²⁶Mg", massNumber: 26, neutrons: 14, abundance: "11.01%", stable: true },
  ],
  13: [
    { symbol: "²⁷Al", massNumber: 27, neutrons: 14, abundance: "100%", stable: true },
    { symbol: "²⁶Al", massNumber: 26, neutrons: 13, halfLife: "7.17×10⁵ y", decayMode: "β⁺", stable: false },
  ],
  14: [
    { symbol: "²⁸Si", massNumber: 28, neutrons: 14, abundance: "92.23%", stable: true },
    { symbol: "²⁹Si", massNumber: 29, neutrons: 15, abundance: "4.67%", stable: true },
    { symbol: "³⁰Si", massNumber: 30, neutrons: 16, abundance: "3.10%", stable: true },
  ],
  15: [
    { symbol: "³¹P", massNumber: 31, neutrons: 16, abundance: "100%", stable: true },
    { symbol: "³²P", massNumber: 32, neutrons: 17, halfLife: "14.28 days", decayMode: "β⁻", stable: false },
    { symbol: "³³P", massNumber: 33, neutrons: 18, halfLife: "25.34 days", decayMode: "β⁻", stable: false },
  ],
  16: [
    { symbol: "³²S", massNumber: 32, neutrons: 16, abundance: "94.99%", stable: true },
    { symbol: "³³S", massNumber: 33, neutrons: 17, abundance: "0.75%", stable: true },
    { symbol: "³⁴S", massNumber: 34, neutrons: 18, abundance: "4.25%", stable: true },
    { symbol: "³⁶S", massNumber: 36, neutrons: 20, abundance: "0.01%", stable: true },
  ],
  17: [
    { symbol: "³⁵Cl", massNumber: 35, neutrons: 18, abundance: "75.76%", stable: true },
    { symbol: "³⁷Cl", massNumber: 37, neutrons: 20, abundance: "24.24%", stable: true },
    { symbol: "³⁶Cl", massNumber: 36, neutrons: 19, halfLife: "3.01×10⁵ y", decayMode: "β⁻", stable: false },
  ],
  18: [
    { symbol: "⁴⁰Ar", massNumber: 40, neutrons: 22, abundance: "99.600%", stable: true },
    { symbol: "³⁶Ar", massNumber: 36, neutrons: 18, abundance: "0.334%", stable: true },
    { symbol: "³⁸Ar", massNumber: 38, neutrons: 20, abundance: "0.063%", stable: true },
  ],
  19: [
    { symbol: "³⁹K", massNumber: 39, neutrons: 20, abundance: "93.258%", stable: true },
    { symbol: "⁴⁰K", massNumber: 40, neutrons: 21, abundance: "0.012%", halfLife: "1.25×10⁹ y", decayMode: "β⁻/EC", stable: false },
    { symbol: "⁴¹K", massNumber: 41, neutrons: 22, abundance: "6.730%", stable: true },
  ],
  20: [
    { symbol: "⁴⁰Ca", massNumber: 40, neutrons: 20, abundance: "96.941%", stable: true },
    { symbol: "⁴²Ca", massNumber: 42, neutrons: 22, abundance: "0.647%", stable: true },
    { symbol: "⁴⁴Ca", massNumber: 44, neutrons: 24, abundance: "2.086%", stable: true },
    { symbol: "⁴⁸Ca", massNumber: 48, neutrons: 28, abundance: "0.187%", halfLife: "6.4×10¹⁹ y", stable: false },
  ],
  26: [
    { symbol: "⁵⁴Fe", massNumber: 54, neutrons: 28, abundance: "5.845%", stable: true },
    { symbol: "⁵⁶Fe", massNumber: 56, neutrons: 30, abundance: "91.754%", stable: true },
    { symbol: "⁵⁷Fe", massNumber: 57, neutrons: 31, abundance: "2.119%", stable: true },
    { symbol: "⁵⁸Fe", massNumber: 58, neutrons: 32, abundance: "0.282%", stable: true },
  ],
  29: [
    { symbol: "⁶³Cu", massNumber: 63, neutrons: 34, abundance: "69.17%", stable: true },
    { symbol: "⁶⁵Cu", massNumber: 65, neutrons: 36, abundance: "30.83%", stable: true },
  ],
  30: [
    { symbol: "⁶⁴Zn", massNumber: 64, neutrons: 34, abundance: "49.17%", stable: true },
    { symbol: "⁶⁶Zn", massNumber: 66, neutrons: 36, abundance: "27.73%", stable: true },
    { symbol: "⁶⁸Zn", massNumber: 68, neutrons: 38, abundance: "18.45%", stable: true },
    { symbol: "⁷⁰Zn", massNumber: 70, neutrons: 40, abundance: "0.61%", stable: true },
  ],
  35: [
    { symbol: "⁷⁹Br", massNumber: 79, neutrons: 44, abundance: "50.69%", stable: true },
    { symbol: "⁸¹Br", massNumber: 81, neutrons: 46, abundance: "49.31%", stable: true },
  ],
  47: [
    { symbol: "¹⁰⁷Ag", massNumber: 107, neutrons: 60, abundance: "51.839%", stable: true },
    { symbol: "¹⁰⁹Ag", massNumber: 109, neutrons: 62, abundance: "48.161%", stable: true },
  ],
  53: [
    { symbol: "¹²⁷I", massNumber: 127, neutrons: 74, abundance: "100%", stable: true },
    { symbol: "¹²⁹I", massNumber: 129, neutrons: 76, halfLife: "1.57×10⁷ y", decayMode: "β⁻", stable: false },
    { symbol: "¹³¹I", massNumber: 131, neutrons: 78, halfLife: "8.02 days", decayMode: "β⁻", stable: false },
  ],
  79: [
    { symbol: "¹⁹⁷Au", massNumber: 197, neutrons: 118, abundance: "100%", stable: true },
    { symbol: "¹⁹⁸Au", massNumber: 198, neutrons: 119, halfLife: "2.695 days", decayMode: "β⁻", stable: false },
  ],
  82: [
    { symbol: "²⁰⁴Pb", massNumber: 204, neutrons: 122, abundance: "1.4%", stable: true },
    { symbol: "²⁰⁶Pb", massNumber: 206, neutrons: 124, abundance: "24.1%", stable: true },
    { symbol: "²⁰⁷Pb", massNumber: 207, neutrons: 125, abundance: "22.1%", stable: true },
    { symbol: "²⁰⁸Pb", massNumber: 208, neutrons: 126, abundance: "52.4%", stable: true },
  ],
  92: [
    { symbol: "²³⁴U", massNumber: 234, neutrons: 142, abundance: "0.0054%", halfLife: "2.455×10⁵ y", decayMode: "α", stable: false },
    { symbol: "²³⁵U", massNumber: 235, neutrons: 143, abundance: "0.7204%", halfLife: "7.038×10⁸ y", decayMode: "α", stable: false },
    { symbol: "²³⁸U", massNumber: 238, neutrons: 146, abundance: "99.274%", halfLife: "4.468×10⁹ y", decayMode: "α", stable: false },
  ],
};

// Detailed reactions with descriptions
export interface ElementReaction {
  equation: string;
  description: string;
  type: string;
}

export const reactionDetails: Record<number, ElementReaction[]> = {
  1: [
    { equation: "2H₂ + O₂ → 2H₂O", description: "Hydrogen burns in oxygen to produce water. Highly exothermic - used in rocket fuel.", type: "Combustion" },
    { equation: "Zn + 2HCl → ZnCl₂ + H₂↑", description: "Zinc reacts with hydrochloric acid, releasing hydrogen gas (collected over water in labs).", type: "Single Replacement" },
    { equation: "H₂ + Cl₂ → 2HCl", description: "Hydrogen reacts with chlorine in UV light to form hydrogen chloride gas.", type: "Synthesis" },
  ],
  3: [
    { equation: "2Li + 2H₂O → 2LiOH + H₂↑", description: "Lithium reacts vigorously with water to produce lithium hydroxide and hydrogen gas.", type: "Single Replacement" },
    { equation: "4Li + O₂ → 2Li₂O", description: "Lithium burns in oxygen with a crimson-red flame to form lithium oxide.", type: "Synthesis" },
    { equation: "2Li + Cl₂ → 2LiCl", description: "Lithium reacts with chlorine gas to form lithium chloride.", type: "Synthesis" },
  ],
  4: [
    { equation: "2Be + O₂ → 2BeO", description: "Beryllium burns in oxygen to form beryllium oxide.", type: "Synthesis" },
    { equation: "Be + 2HCl → BeCl₂ + H₂↑", description: "Beryllium dissolves in hydrochloric acid, releasing hydrogen.", type: "Single Replacement" },
  ],
  5: [
    { equation: "4B + 3O₂ → 2B₂O₃", description: "Boron burns at high temperatures to form boron trioxide.", type: "Synthesis" },
  ],
  6: [
    { equation: "C + O₂ → CO₂", description: "Carbon burns completely in oxygen to form carbon dioxide.", type: "Combustion" },
    { equation: "2C + O₂ → 2CO", description: "With limited oxygen, carbon forms toxic carbon monoxide.", type: "Combustion" },
    { equation: "CH₄ + 2O₂ → CO₂ + 2H₂O", description: "Methane (natural gas) combustion - primary energy source worldwide.", type: "Combustion" },
  ],
  7: [
    { equation: "N₂ + 3H₂ → 2NH₃", description: "The Haber process: nitrogen and hydrogen form ammonia under high pressure/temperature with a catalyst.", type: "Synthesis" },
    { equation: "N₂ + O₂ → 2NO", description: "At very high temperatures (lightning), nitrogen and oxygen combine to form nitric oxide.", type: "Synthesis" },
  ],
  8: [
    { equation: "2Mg + O₂ → 2MgO", description: "Magnesium burns with a blinding white flame in oxygen to form magnesium oxide.", type: "Synthesis" },
    { equation: "2H₂ + O₂ → 2H₂O", description: "Hydrogen and oxygen combine to form water - the basis of fuel cells.", type: "Synthesis" },
    { equation: "4Fe + 3O₂ → 2Fe₂O₃", description: "Iron slowly rusts in moist air, forming iron(III) oxide.", type: "Synthesis" },
  ],
  9: [
    { equation: "2F₂ + 2H₂O → 4HF + O₂", description: "Fluorine is so reactive it decomposes water, releasing oxygen.", type: "Single Replacement" },
    { equation: "H₂ + F₂ → 2HF", description: "Hydrogen and fluorine react explosively, even in the dark.", type: "Synthesis" },
  ],
  11: [
    { equation: "2Na + 2H₂O → 2NaOH + H₂↑", description: "Sodium reacts violently with water - the metal skates on the surface, may ignite with a yellow flame.", type: "Single Replacement" },
    { equation: "2Na + Cl₂ → 2NaCl", description: "Sodium burns in chlorine gas with a bright yellow flame to form table salt.", type: "Synthesis" },
    { equation: "2Na + O₂ → Na₂O₂", description: "Sodium reacts with excess oxygen to form sodium peroxide.", type: "Synthesis" },
  ],
  12: [
    { equation: "2Mg + O₂ → 2MgO", description: "Magnesium ribbon burns with an intense white light - used in fireworks and flares.", type: "Synthesis" },
    { equation: "Mg + 2HCl → MgCl₂ + H₂↑", description: "Magnesium dissolves readily in hydrochloric acid with vigorous bubbling.", type: "Single Replacement" },
    { equation: "Mg + CuSO₄ → MgSO₄ + Cu", description: "Magnesium displaces copper from copper sulfate solution (blue → colorless).", type: "Single Replacement" },
  ],
  13: [
    { equation: "4Al + 3O₂ → 2Al₂O₃", description: "Aluminum burns at high temperature. The oxide layer normally protects it from corrosion.", type: "Synthesis" },
    { equation: "2Al + 6HCl → 2AlCl₃ + 3H₂↑", description: "Aluminum dissolves in hydrochloric acid, releasing hydrogen gas.", type: "Single Replacement" },
    { equation: "2Al + Fe₂O₃ → Al₂O₃ + 2Fe", description: "The thermite reaction - produces molten iron with intense heat (~2500°C).", type: "Single Replacement" },
  ],
  14: [
    { equation: "Si + O₂ → SiO₂", description: "Silicon burns in oxygen to form silicon dioxide (quartz/sand).", type: "Synthesis" },
    { equation: "Si + 2Cl₂ → SiCl₄", description: "Silicon reacts with chlorine gas to form silicon tetrachloride.", type: "Synthesis" },
  ],
  15: [
    { equation: "P₄ + 5O₂ → P₄O₁₀", description: "White phosphorus burns spontaneously in air with a bright flame.", type: "Combustion" },
    { equation: "P₄O₁₀ + 6H₂O → 4H₃PO₄", description: "Phosphorus pentoxide reacts vigorously with water to form phosphoric acid.", type: "Synthesis" },
  ],
  16: [
    { equation: "S + O₂ → SO₂", description: "Sulfur burns with a blue flame in air to produce sulfur dioxide (pungent smell).", type: "Combustion" },
    { equation: "2H₂S + SO₂ → 3S + 2H₂O", description: "Hydrogen sulfide and sulfur dioxide react to deposit solid sulfur.", type: "Double Replacement" },
  ],
  17: [
    { equation: "2Na + Cl₂ → 2NaCl", description: "Sodium burns in chlorine gas to form sodium chloride (table salt).", type: "Synthesis" },
    { equation: "H₂ + Cl₂ → 2HCl", description: "Hydrogen and chlorine react in UV light (explosive in sunlight).", type: "Synthesis" },
    { equation: "Cl₂ + 2KBr → 2KCl + Br₂", description: "Chlorine displaces bromine from potassium bromide (solution turns brown).", type: "Single Replacement" },
  ],
  19: [
    { equation: "2K + 2H₂O → 2KOH + H₂↑", description: "Potassium reacts explosively with water - lilac/violet flame, may detonate.", type: "Single Replacement" },
    { equation: "4K + O₂ → 2K₂O", description: "Potassium rapidly oxidizes in air and can ignite spontaneously.", type: "Synthesis" },
  ],
  20: [
    { equation: "Ca + 2H₂O → Ca(OH)₂ + H₂↑", description: "Calcium reacts steadily with water, producing a milky limewater suspension.", type: "Single Replacement" },
    { equation: "CaCO₃ → CaO + CO₂", description: "Heating limestone (calcium carbonate) produces quicklime and CO₂ - used in cement.", type: "Decomposition" },
    { equation: "CaO + H₂O → Ca(OH)₂", description: "Quicklime reacts exothermically with water to form slaked lime.", type: "Synthesis" },
  ],
  24: [
    { equation: "4Cr + 3O₂ → 2Cr₂O₃", description: "Chromium reacts with oxygen at high temperature to form green chromium(III) oxide.", type: "Synthesis" },
    { equation: "2Cr + 6HCl → 2CrCl₃ + 3H₂↑", description: "Chromium dissolves in dilute hydrochloric acid.", type: "Single Replacement" },
  ],
  25: [
    { equation: "2Mn + O₂ → 2MnO", description: "Manganese burns in oxygen to form manganese(II) oxide.", type: "Synthesis" },
    { equation: "MnO₂ + 4HCl → MnCl₂ + Cl₂ + 2H₂O", description: "MnO₂ reacts with concentrated HCl to produce chlorine gas (lab preparation).", type: "Redox" },
  ],
  26: [
    { equation: "4Fe + 3O₂ → 2Fe₂O₃", description: "Iron rusts in moist air, forming reddish-brown iron(III) oxide.", type: "Synthesis" },
    { equation: "Fe + CuSO₄ → FeSO₄ + Cu", description: "Iron displaces copper from copper sulfate - nail turns coppery, solution turns green.", type: "Single Replacement" },
    { equation: "Fe + 2HCl → FeCl₂ + H₂↑", description: "Iron dissolves in dilute hydrochloric acid, releasing hydrogen gas.", type: "Single Replacement" },
  ],
  29: [
    { equation: "Cu + 2AgNO₃ → Cu(NO₃)₂ + 2Ag", description: "Copper displaces silver - silver crystals grow on copper surface, solution turns blue.", type: "Single Replacement" },
    { equation: "2Cu + O₂ → 2CuO", description: "Copper slowly oxidizes in air, forming black copper(II) oxide when heated.", type: "Synthesis" },
  ],
  30: [
    { equation: "Zn + 2HCl → ZnCl₂ + H₂↑", description: "Zinc readily dissolves in hydrochloric acid with vigorous effervescence.", type: "Single Replacement" },
    { equation: "Zn + CuSO₄ → ZnSO₄ + Cu", description: "Zinc displaces copper from copper sulfate solution (blue → colorless).", type: "Single Replacement" },
  ],
  35: [
    { equation: "Br₂ + 2KI → 2KBr + I₂", description: "Bromine displaces iodine from potassium iodide (solution turns brown/dark).", type: "Single Replacement" },
    { equation: "H₂ + Br₂ → 2HBr", description: "Hydrogen and bromine react to form hydrogen bromide.", type: "Synthesis" },
  ],
  47: [
    { equation: "3Ag + 4HNO₃ → 3AgNO₃ + NO↑ + 2H₂O", description: "Silver dissolves only in oxidizing acids like nitric acid.", type: "Redox" },
    { equation: "2Ag + S → Ag₂S", description: "Silver tarnishes in air containing sulfur compounds, forming black silver sulfide.", type: "Synthesis" },
  ],
  53: [
    { equation: "I₂ + Starch → Blue-black complex", description: "Iodine forms a deep blue-black color with starch - classic test for starch.", type: "Complex formation" },
    { equation: "2Al + 3I₂ → 2AlI₃", description: "Aluminum and iodine react exothermically with a drop of water as catalyst.", type: "Synthesis" },
  ],
  79: [
    { equation: "Au + 3HCl + HNO₃ → HAuCl₄ + NO↑ + 2H₂O", description: "Gold dissolves only in aqua regia (3:1 HCl:HNO₃) - 'royal water'.", type: "Redox" },
  ],
  82: [
    { equation: "2Pb + O₂ → 2PbO", description: "Lead slowly oxidizes in air to form lead(II) oxide (litharge).", type: "Synthesis" },
    { equation: "Pb + 4HNO₃ → Pb(NO₃)₂ + 2NO₂↑ + 2H₂O", description: "Lead dissolves in dilute nitric acid, producing brown nitrogen dioxide.", type: "Redox" },
  ],
  92: [
    { equation: "U + 2F₂ → UF₄", description: "Uranium reacts with fluorine - uranium hexafluoride (UF₆) is used for enrichment.", type: "Synthesis" },
    { equation: "3U + 4O₂ → U₃O₈", description: "Uranium oxidizes in air - triuranium octoxide is the most stable oxide.", type: "Synthesis" },
  ],
};
